import React, { useState } from 'react';
import { CircularProgress, Dialog, Input, TextField } from '@mui/material';
import { IconCheck, IconX } from '@tabler/icons-react';
import { buildClaimUrl } from '../../../utils/magicLink';
import { useToast } from '../../../common/toast/toastContext';

interface GuestInvitationSectionProps {
  guestNumber: number;
  email: string;
  inviteId: number | undefined;
  magicLink?: string;
  isSending: boolean;
  isConfirmDeactivateOpen: boolean;
  inviteActive: boolean;
  onEmailChange: (email: string) => void;
  onSendInvitation: () => void;
  onSwitchToManual: () => void;
  onClose: () => void;
  onOpenDeactivateConfirm: () => void;
  onCloseDeactivateConfirm: () => void;
  onConfirmDeactivate: () => void;
}

const GuestInvitationSection: React.FC<GuestInvitationSectionProps> = ({
  guestNumber,
  email,
  inviteId,
  magicLink,
  isSending,
  isConfirmDeactivateOpen,
  inviteActive,
  onEmailChange,
  onSendInvitation,
  onSwitchToManual,
  onClose,
  onOpenDeactivateConfirm,
  onCloseDeactivateConfirm,
  onConfirmDeactivate,
}) => {
  const { showToast } = useToast();
  const [isCopying, setIsCopying] = useState(false);

  const handleCopyInvitationLink = async () => {
    if (!magicLink) {
      showToast({
        message: 'Invitation link is not available yet. Resend the invite.',
        severity: 'warning',
      });
      return;
    }
    setIsCopying(true);
    try {
      await navigator.clipboard.writeText(buildClaimUrl(magicLink, email));
      showToast({
        message: 'Invitation link copied.',
        severity: 'success',
      });
    } catch {
      showToast({
        message: 'Could not copy the link. Try again.',
        severity: 'error',
      });
    } finally {
      setIsCopying(false);
    }
  };

  const renderInviteSuccessState = () => {
    return (
      <div className="flex flex-col items-start pb-6 pl-6 pr-14 pt-5">
        <div className="flex items-center">
          <IconCheck size={28} className="text-status-success" />
          <span className="ml-2 text-xl font-semibold text-status-success">
            Invitation sent to Guest {guestNumber}
          </span>
        </div>
        <p className="mt-3 text-neutral-800">
          We&apos;ve sent an invitation to your guest {guestNumber} email
          address to complete their trip details. Please wait for them to
          provide the necessary information.
        </p>
        <div className="mt-2 p-3 border bg-neutral-100 ">
          <Input fullWidth value={email} readOnly />
        </div>
        <p className="mt-3 text-neutral-700 text-sm">
          Need to update the email or enter details yourself? You can deactivate
          the current invitation link.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            className="underline text-neutral-800 font-semibold"
            onClick={onOpenDeactivateConfirm}
          >
            Deactivate invitation link
          </button>
          <button type="button" onClick={onClose}>
            Close
          </button>
          <button
            type="button"
            className="underline text-neutral-800 font-semibold"
            onClick={() => {
              void handleCopyInvitationLink();
            }}
            disabled={isCopying}
          >
            {isCopying ? 'Copying…' : 'Copy invitation link'}
          </button>
        </div>
      </div>
    );
  };

  const renderInvitationForm = () => {
    return (
      <div className="flex flex-col pb-6 pl-6 pr-14 pt-5">
        <div className="flex flex-col">
          <span className="text-2xl font-semibold text-neutral-800">
            Invite{' '}
            <span className="text-brand-santtai-primary">
              Guest {guestNumber}
            </span>{' '}
            to provide their details
          </span>
          <span className="mt-3 text-neutral-500">
            Please enter guest {guestNumber}&apos;s email to ensure all
            necessary trip information is complete.
          </span>
        </div>
        <div className="mt-6 w-full">
          <TextField
            fullWidth
            id="guest-invite-email"
            label="Email address"
            variant="outlined"
            value={email}
            onChange={e => onEmailChange(e.target.value)}
          />
        </div>
        <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            className="w-full font-semibold text-neutral-800 underline sm:w-auto"
            onClick={onSwitchToManual}
          >
            Skip and fill in manually
          </button>
          <div className="flex w-full justify-end gap-3 sm:w-auto">
            <button
              type="button"
              className="hidden h-11 rounded-lg border border-neutral-800 bg-neutral-00 px-4 font-semibold text-neutral-800 md:block"
              onClick={onClose}
            >
              Close
            </button>
            <button
              type="button"
              className="h-11 w-full rounded-lg bg-neutral-800 px-4 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              disabled={isSending || !email}
              onClick={onSendInvitation}
            >
              {isSending ? (
                <div className="flex items-center justify-center">
                  <CircularProgress size={18} color="inherit" />
                  <span className="ml-2">Sending...</span>
                </div>
              ) : (
                'Send invitation'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const showSuccess = Boolean(inviteId && inviteActive);

  return (
    <>
      {showSuccess && renderInviteSuccessState()}
      {!showSuccess && renderInvitationForm()}

      <Dialog
        open={isConfirmDeactivateOpen}
        onClose={onCloseDeactivateConfirm}
        aria-labelledby="deactivate-dialog-title"
        aria-describedby="deactivate-dialog-description"
        slotProps={{
          paper: { sx: { borderRadius: '24px', maxWidth: '480px', p: 3 } },
        }}
      >
        <div className="flex justify-between items-center">
          <h2 id="deactivate-dialog-title" className="text-xl font-semibold">
            Confirm deactivation
          </h2>
          <button
            type="button"
            aria-label="Close deactivation dialog"
            onClick={onCloseDeactivateConfirm}
          >
            <IconX size={22} />
          </button>
        </div>
        <p id="deactivate-dialog-description" className="mt-2 text-neutral-700">
          Are you sure you want to deactivate the invitation link? Guests who
          open this claim link will be blocked in this browser, and you can send
          a new invite.
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            className="border border-neutral-400 text-neutral-800 px-4 py-2 rounded"
            onClick={onCloseDeactivateConfirm}
          >
            Close
          </button>
          <button
            type="button"
            className="bg-neutral-800 text-white px-4 py-2 rounded"
            onClick={onConfirmDeactivate}
          >
            Confirm
          </button>
        </div>
      </Dialog>
    </>
  );
};

export default GuestInvitationSection;
